import classNames from "classnames";
import BadgesList from "components/BadgesList";
import { Separator } from "components/Separator";
import { StatDisplay } from "components/StatDisplay";
import Tag from "components/Tag";
import { DOI_URL } from "constants/config";
import { Badges, Paper } from "helpers/api";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import { ReactNode } from "react";
import { PaperMetadataSkeleton } from "./";
import { BookOpen } from "lucide-react";
import { FullTextIcon } from "design_system";
import Tooltip from "components/Tooltip";

const MAX_JOURNAL_CHARACTERS = 32;

type PaperMetadataProps = {
  paper: Paper;
  showDoi?: boolean;
  isProcessingMetadata?: boolean;
  hideCitations?: boolean;
  isCompact?: boolean;
  badges?: Badges;
  extraTags?: ReactNode[];
  showUsedFullTextTag?: boolean;
  useTwoRowLayout?: boolean;
};

/**
 * @component PaperMetadata
 * @description Inline list of paper metadata for a Search Result.
 * Truncation priority: journal truncates before authors
 * Can use single or two row layout
 */
function PaperMetadata({
  paper,
  showDoi = false,
  isProcessingMetadata = false,
  hideCitations = false,
  isCompact = false,
  badges,
  extraTags,
  showUsedFullTextTag,
  useTwoRowLayout = false,
}: PaperMetadataProps) {
  const [paperMetadataLabels] = useLabels("paper-metadata");
  const [fullTextLabels] = useLabels("tooltips.used-full-text");

  if (isProcessingMetadata) {
    return <PaperMetadataSkeleton />;
  }

  return (
    <div className="@container flex-1">
      {showUsedFullTextTag && (
        <Tag
          label={fullTextLabels["tag"]}
          icon={<FullTextIcon size="sm" />}
          className="mb-3"
        />
      )}

      {/* First row */}
      <div className="flex items-center gap-[6px] min-w-0">
        {/* Year */}
        <span className="flex-none tiny-mono-bold text-fg-base">
          {paper.year}
        </span>

        {/* Citations */}
        {!hideCitations && (
          <>
            <Separator />
            <StatDisplay
              count={paper.citation_count ?? 0}
              label={pluralize(paperMetadataLabels["citation"], {
                count: paper.citation_count ?? 0,
              })}
            />
          </>
        )}

        <Separator />

        {/* Authors - can shrink but with lower priority than journal */}
        <span className="min-w-0 whitespace-nowrap tiny-normal text-fg-muted text-ellipsis overflow-hidden flex-shrink-0.5">
          {paper.primary_author}{" "}
          {paper.authors?.length > 1 && paperMetadataLabels["et-al"]}
        </span>

        {/* If not using two row layout, show journal, DOI, and badges in the first row*/}
        {!useTwoRowLayout && (
          <>
            {/* Journal */}
            {paper.journal && (
              <>
                <Separator className="hidden @lg:block" />
                <BookOpen className="hidden @lg:block w-3.5 h-3.5 text-fg-muted flex-none" />
                <Tooltip
                  content={<span className="text-center">{paper.journal}</span>}
                  placement="top"
                  isDisabled={paper.journal.length <= MAX_JOURNAL_CHARACTERS}
                >
                  <span className="hidden @lg:block italic tiny-normal text-fg-muted overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-shrink-2">
                    {paper.journal.length > MAX_JOURNAL_CHARACTERS
                      ? `${paper.journal.slice(0, MAX_JOURNAL_CHARACTERS)}...`
                      : paper.journal}
                  </span>
                </Tooltip>
              </>
            )}

            {/* DOI */}
            {showDoi && (
              <>
                <Separator className="hidden @lg:block" />
                <a
                  className="hidden @lg:flex tiny-normal items-center gap-[6px] flex-none"
                  href={`${DOI_URL}/${paper.doi}`}
                  target="_blank"
                  data-testid="doi-link"
                >
                  <span>{paperMetadataLabels["DOI"]}</span>
                  <span className="icon-external-link"></span>
                </a>
              </>
            )}

            {/* Badges */}
            {isCompact && (
              <span className="hidden @lg:flex gap-[6px] items-center flex-none text-fg-muted">
                <BadgesList
                  badges={badges}
                  extraTags={extraTags}
                  isCompact={true}
                  includeSeparator={true}
                />
              </span>
            )}
          </>
        )}
      </div>

      {/* Second row */}
      {useTwoRowLayout && (
        <div className="flex items-center gap-[6px] min-w-0 mt-0.5">
          {/* Journal */}
          {paper.journal && (
            <>
              <BookOpen className="w-3.5 h-3.5 text-fg-muted" />
              <span className="overflow-hidden min-w-0 italic whitespace-nowrap tiny-normal text-fg-muted text-ellipsis flex-shrink">
                {paper.journal}
              </span>
            </>
          )}

          {/* Badges */}
          <div className="flex items-center gap-[6px] text-fg-muted">
            <BadgesList
              badges={badges}
              extraTags={extraTags}
              isCompact={true}
              includeSeparator={!!paper.journal}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PaperMetadata;
